/**
 * Hidden demo command for the Cmd+K palette (`EntityQuickFind`).
 *
 * THROWAWAY DEMO CODE. Renders nothing unless the user types something matching
 * the demo trigger (see `matchesDemoTrigger`). Demo mode is a single global
 * switch that affects every demo at once: when off it offers "Start Demo"; when
 * on it offers "Reset Demo" (re-seed all fixtures in place) and "Exit Demo"
 * (reload into the real app). Delete with `frontend/src/demo/command/` and
 * unwire the two lines in `EntityQuickFind.tsx`.
 */

import { useCallback } from 'react';
import {
  ArrowCounterClockwiseIcon,
  SignOutIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import {
  DEMO_CATALOG,
  exitDemo,
  isDemoModeOn,
  matchesDemoTrigger,
  resetAllDemos,
  startDemo,
} from './demo-catalog';

interface DemoCommandItemsProps {
  /** The debounced palette search; the group only renders when it matches. */
  search: string;
  /** Close + clear the palette (used by Reset, which doesn't reload). */
  onClose: () => void;
}

export function DemoCommandItems({ search, onClose }: DemoCommandItemsProps) {
  const queryClient = useQueryClient();
  const on = isDemoModeOn();

  const handleReset = useCallback(() => {
    resetAllDemos();
    // Drop cached real-query data so the freshly-seeded fixtures take over.
    void queryClient.invalidateQueries();
    onClose();
  }, [queryClient, onClose]);

  if (!matchesDemoTrigger(search)) return null;

  return (
    <CommandGroup heading="Demo">
      {on ? (
        <>
          <CommandItem value="demo reset" onSelect={handleReset}>
            <ArrowCounterClockwiseIcon className="size-4 shrink-0" />
            <span>Reset Demo</span>
          </CommandItem>
          <CommandItem value="demo exit stop" onSelect={() => exitDemo()}>
            <SignOutIcon className="size-4 shrink-0" />
            <span>Exit Demo</span>
          </CommandItem>
        </>
      ) : (
        <CommandItem value="demo start" onSelect={() => startDemo()}>
          <SparkleIcon weight="fill" className="size-4 shrink-0" />
          <span>Start Demo</span>
          <span className="text-muted-foreground ml-auto text-xs truncate">
            {DEMO_CATALOG.map((demo) => demo.label).join(' · ')}
          </span>
        </CommandItem>
      )}
    </CommandGroup>
  );
}
